import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import api from '../../services/api';

const STATUS_TABS = [
  { id: 'pending', label: 'Pending approval' },
  { id: 'approved', label: 'Live' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'paused', label: 'Paused' },
  { id: 'all', label: 'All Mitra' },
];

const statusColor = (s) => {
  if (s === 'approved') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'rejected') return 'error';
  if (s === 'paused') return 'default';
  return 'info';
};

export default function LearnZoneApprovals() {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rejectOpen, setRejectOpen] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busyCode, setBusyCode] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/local-learn/admin/courses', {
        params: { status: tab },
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const moderate = async (courseCode, action, reason) => {
    setBusyCode(courseCode);
    try {
      await api.patch(`/local-learn/admin/courses/${encodeURIComponent(courseCode)}/moderate`, {
        action,
        reason,
      });
      setRejectOpen(null);
      setRejectReason('');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Action failed');
    } finally {
      setBusyCode('');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <SchoolOutlinedIcon color="primary" />
        <Typography variant="h5" fontWeight={800}>
          Learn Zone Approvals
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Local Tutors (Mitra) must get app-admin approval before courses appear in Citizen
        Learn Zone / Online Courses / Campus Classes. KNLS Academy courses are not listed
        here and stay untouched.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Legal tip: approve only after checking no false KNLS affiliation, clear Online vs
        Campus prices, and tutor contact / campus location for campus modes.
      </Alert>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        sx={{ mb: 2 }}
      >
        {STATUS_TABS.map((t) => (
          <Tab key={t.id} value={t.id} label={t.label} />
        ))}
      </Tabs>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No courses in this queue.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((c) => (
            <Paper key={c.courseCode} sx={{ p: 2 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
              >
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography fontWeight={700}>{c.courseName}</Typography>
                    <Chip size="small" label={c.marketplaceStatus} color={statusColor(c.marketplaceStatus)} />
                    <Chip size="small" variant="outlined" label={c.courseCode} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {c.tutorDisplayName || 'Tutor'} · {c.campusCity || 'Town n/a'} ·{' '}
                    {[c.offersOnline && `Online ₹${c.onlineFee}`, c.offersCampus && `Campus ₹${c.campusFee}`]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                  {c.shortPitch ? (
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                      {c.shortPitch}
                    </Typography>
                  ) : null}
                  {c.rejectionReason ? (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {c.rejectionReason}
                    </Alert>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="flex-start">
                  {c.marketplaceStatus === 'pending' || c.marketplaceStatus === 'rejected' ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleOutlineIcon />}
                      disabled={busyCode === c.courseCode}
                      onClick={() => moderate(c.courseCode, 'approve')}
                    >
                      Approve
                    </Button>
                  ) : null}
                  {c.marketplaceStatus === 'pending' ? (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<HighlightOffIcon />}
                      disabled={busyCode === c.courseCode}
                      onClick={() => setRejectOpen(c.courseCode)}
                    >
                      Reject
                    </Button>
                  ) : null}
                  {c.marketplaceStatus === 'approved' ? (
                    <Button
                      size="small"
                      startIcon={<PauseCircleOutlineIcon />}
                      disabled={busyCode === c.courseCode}
                      onClick={() => moderate(c.courseCode, 'pause')}
                    >
                      Pause
                    </Button>
                  ) : null}
                  {c.marketplaceStatus === 'paused' ? (
                    <Button
                      size="small"
                      startIcon={<PlayCircleOutlineIcon />}
                      disabled={busyCode === c.courseCode}
                      onClick={() => moderate(c.courseCode, 'resume')}
                    >
                      Resume
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={Boolean(rejectOpen)} onClose={() => setRejectOpen(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reject course</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            margin="dense"
            label="Reason (shown to Mitra)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <TextField
            select
            fullWidth
            margin="dense"
            label="Quick reason"
            value=""
            onChange={(e) => setRejectReason(e.target.value)}
          >
            <MenuItem value="Missing campus address / city">Missing campus address / city</MenuItem>
            <MenuItem value="Unclear Online vs Campus pricing">Unclear Online vs Campus pricing</MenuItem>
            <MenuItem value="Content appears to claim KNLS affiliation">
              Content appears to claim KNLS affiliation
            </MenuItem>
            <MenuItem value="Insufficient course details / syllabus">
              Insufficient course details / syllabus
            </MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectOpen}
            onClick={() => moderate(rejectOpen, 'reject', rejectReason)}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
